import React, { useState, useEffect, useRef, useMemo } from 'react';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import {
  Layers,
  Plus,
  Minus,
  RotateCcw,
  Upload,
  Eye,
  Sliders,
  Box,
  Sparkles,
  Sun,
  Grid as GridIcon,
  Ruler,
  Info,
  Compass,
  Video,
  ArrowUp,
  ArrowDown,
  ArrowLeft,
  ArrowRight,
  RotateCw,
} from 'lucide-react';
import './frame3d.css';
import baseLayer from '../assets/5_base.glb';
import firstLayer from '../assets/5_first.glb';
import speakerLayer from '../assets/5_speaker.glb';
import twoLayer from '../assets/5_2M.glb';
import topLayer from '../assets/5_top.glb';

const createProceduralGroup = (type) => {
  const group = new THREE.Group();

  switch (type) {
    case 1: {
      // Heavy Base Plate / Foundation Block
      const baseGeo = new THREE.BoxGeometry(1.8, 0.4, 1.8);
      const baseMat = new THREE.MeshStandardMaterial({
        color: 0x334155,
        roughness: 0.3,
        metalness: 0.8,
      });
      const baseMesh = new THREE.Mesh(baseGeo, baseMat);
      baseMesh.position.y = 0.2;
      group.add(baseMesh);

      const boltGeo = new THREE.CylinderGeometry(0.08, 0.08, 0.1, 16);
      const boltMat = new THREE.MeshStandardMaterial({
        color: 0x94a3b8,
        metalness: 0.9,
        roughness: 0.2,
      });
      [
        [-0.7, -0.7],
        [0.7, -0.7],
        [-0.7, 0.7],
        [0.7, 0.7],
      ].forEach(([x, z]) => {
        const bolt = new THREE.Mesh(boltGeo, boltMat);
        bolt.position.set(x, 0.45, z);
        group.add(bolt);
      });
      break;
    }
    case 2: {
      // Industrial Riser Hub
      const cylGeo = new THREE.CylinderGeometry(0.6, 0.75, 0.7, 32);
      const cylMat = new THREE.MeshStandardMaterial({
        color: 0x0284c7,
        roughness: 0.4,
        metalness: 0.6,
      });
      const cylMesh = new THREE.Mesh(cylGeo, cylMat);
      cylMesh.position.y = 0.35;
      group.add(cylMesh);

      const ringGeo = new THREE.TorusGeometry(0.65, 0.05, 16, 32);
      const ringMat = new THREE.MeshStandardMaterial({
        color: 0x38bdf8,
        metalness: 0.8,
      });
      const ring = new THREE.Mesh(ringGeo, ringMat);
      ring.rotation.x = Math.PI / 2;
      ring.position.y = 0.5;
      group.add(ring);
      break;
    }
    case 3: {
      // Modular Middle Column Component (Repeated)
      const bodyGeo = new THREE.CylinderGeometry(0.45, 0.45, 0.75, 24);
      const bodyMat = new THREE.MeshStandardMaterial({
        color: 0xf59e0b,
        roughness: 0.3,
        metalness: 0.5,
      });
      const body = new THREE.Mesh(bodyGeo, bodyMat);
      body.position.y = 0.375;
      group.add(body);

      const ribGeo = new THREE.TorusGeometry(0.47, 0.03, 12, 24);
      const ribMat = new THREE.MeshStandardMaterial({
        color: 0xd97706,
        roughness: 0.5,
      });
      [0.15, 0.375, 0.6].forEach((yPos) => {
        const rib = new THREE.Mesh(ribGeo, ribMat);
        rib.rotation.x = Math.PI / 2;
        rib.position.y = yPos;
        group.add(rib);
      });

      const notchGeo = new THREE.BoxGeometry(0.1, 0.3, 0.1);
      const notchMat = new THREE.MeshStandardMaterial({
        color: 0xef4444,
        roughness: 0.2,
      });
      const notch = new THREE.Mesh(notchGeo, notchMat);
      notch.position.set(0.42, 0.375, 0);
      group.add(notch);
      break;
    }
    case 4: {
      // Upper Adapter Chamber
      const mainGeo = new THREE.ConeGeometry(0.7, 0.8, 32);
      const mainMat = new THREE.MeshStandardMaterial({
        color: 0x10b981,
        roughness: 0.3,
        metalness: 0.7,
      });
      const mainMesh = new THREE.Mesh(mainGeo, mainMat);
      mainMesh.position.y = 0.4;
      group.add(mainMesh);

      const topPlate = new THREE.Mesh(
        new THREE.CylinderGeometry(0.3, 0.3, 0.1, 24),
        new THREE.MeshStandardMaterial({ color: 0x047857, metalness: 0.9 }),
      );
      topPlate.position.y = 0.75;
      group.add(topPlate);
      break;
    }
    case 5: {
      // Top Beacon / Terminal Unit
      const capGeo = new THREE.SphereGeometry(0.45, 32, 16);
      const capMat = new THREE.MeshStandardMaterial({
        color: 0x6366f1,
        roughness: 0.1,
        metalness: 0.9,
        emissive: 0x312e81,
        emissiveIntensity: 0.3,
      });
      const capMesh = new THREE.Mesh(capGeo, capMat);
      capMesh.position.y = 0.45;
      group.add(capMesh);

      const antennaGeo = new THREE.CylinderGeometry(0.02, 0.04, 0.4, 16);
      const antennaMat = new THREE.MeshStandardMaterial({
        color: 0xe0e7ff,
        metalness: 0.9,
      });
      const antenna = new THREE.Mesh(antennaGeo, antennaMat);
      antenna.position.y = 0.85;
      group.add(antenna);
      break;
    }
    default: {
      const box = new THREE.Mesh(
        new THREE.BoxGeometry(0.8, 0.8, 0.8),
        new THREE.MeshStandardMaterial({ color: 0x94a3b8 }),
      );
      box.position.y = 0.4;
      group.add(box);
    }
  }

  group.traverse((child) => {
    if (child.isMesh) {
      child.castShadow = true;
      child.receiveShadow = true;
    }
  });

  return group;
};

export default function Frame3d() {
  const mountRef = useRef(null);

  // App State
  const [comp3Count, setComp3Count] = useState(3);
  const [explodeView, setExplodeView] = useState(false);
  const [wireframe, setWireframe] = useState(false);
  const [showGrid, setShowGrid] = useState(true);
  const [showDimensions, setShowDimensions] = useState(false);
  const [autoRotate, setAutoRotate] = useState(false);
  const [lightIntensity, setLightIntensity] = useState(1.2);

  // Camera & View Presets
  const [cameraMode, setCameraMode] = useState('ORTHO');
  const [viewPreset, setViewPreset] = useState('ISO');
  const [activeTab, setActiveTab] = useState('views');

  // Model Sources
  const [modelSources, setModelSources] = useState({
    1: { name: 'Base Foundation Block (Mod 1)', url: '', isCustom: false },
    2: { name: 'Lower Riser Hub (Mod 2)', url: '', isCustom: false },
    3: { name: 'Modular Column Segment (Mod 3)', url: '', isCustom: false },
    4: { name: 'Adapter Transition Box (Mod 4)', url: '', isCustom: false },
    5: { name: 'Top Terminal Cap (Mod 5)', url: '', isCustom: false },
  });

  const loadedGltfScenes = useRef({});

  useEffect(() => {
    const loader = new GLTFLoader();
    loader.load(
      baseLayer, //'/5_base.glb',
      (gltf) => {
        loadedGltfScenes.current[1] = gltf.scene;
        setModelSources((prev) => ({
          ...prev,
          1: {
            ...prev[1],
            url: baseLayer,
            isCustom: true,
            fileName: '5_base.glb',
          },
        }));
      },
      undefined,
      (err) => {
        console.warn(
          'Could not load /5_base.glb automatically, falling back to procedural model.',
          err,
        );
      },
    );
    loader.load(
      firstLayer, //'/5_first.glb',
      (gltf) => {
        loadedGltfScenes.current[2] = gltf.scene;
        setModelSources((prev) => ({
          ...prev,
          2: {
            ...prev[2],
            url: firstLayer,
            isCustom: true,
            fileName: '5_first.glb',
          },
        }));
      },
      undefined,
      (err) => {
        console.warn(
          'Could not load /5_first.glb automatically, falling back to procedural model.',
          err,
        );
      },
    );
    loader.load(
      speakerLayer, //'/5_speaker.glb',
      (gltf) => {
        loadedGltfScenes.current[3] = gltf.scene;
        setModelSources((prev) => ({
          ...prev,
          3: {
            ...prev[3],
            url: speakerLayer,
            isCustom: true,
            fileName: '5_speaker.glb',
          },
        }));
      },
      undefined,
      (err) => {
        console.warn(
          'Could not load /5_speaker.glb automatically, falling back to procedural model.',
          err,
        );
      },
    );
    loader.load(
      twoLayer, //'/5_2M.glb',
      (gltf) => {
        loadedGltfScenes.current[4] = gltf.scene;
        setModelSources((prev) => ({
          ...prev,
          4: {
            ...prev[4],
            url: twoLayer,
            isCustom: true,
            fileName: '5_2M.glb',
          },
        }));
      },
      undefined,
      (err) => {
        console.warn(
          'Could not load /5_2M.glb automatically, falling back to procedural model.',
          err,
        );
      },
    );
    loader.load(
      topLayer, //'/5_top.glb',
      (gltf) => {
        loadedGltfScenes.current[5] = gltf.scene;
        setModelSources((prev) => ({
          ...prev,
          5: {
            ...prev[5],
            url: topLayer,
            isCustom: true,
            fileName: '5_top.glb',
          },
        }));
      },
      undefined,
      (err) => {
        console.warn(
          'Could not load /5_top.glb automatically, falling back to procedural model.',
          err,
        );
      },
    );
  }, []);

  // Three.js Scene References
  const sceneRef = useRef(null);
  const cameraOrthoRef = useRef(null);
  const cameraPerspRef = useRef(null);
  const activeCameraRef = useRef(null);
  const rendererRef = useRef(null);
  const controlsRef = useRef(null);
  const stackGroupRef = useRef(null);
  const gridHelperRef = useRef(null);
  const dirLightRef = useRef(null);
  const ambLightRef = useRef(null);
  const dimensionsGroupRef = useRef(null);

  const cameraTargetPos = useRef(new THREE.Vector3(10, 8, 10));
  const cameraTargetLookAt = useRef(new THREE.Vector3(0, 0, 0));
  const targetOrthoZoom = useRef(1);

  const stackItems = useMemo(() => {
    const items = [];
    let currentY = 0;
    const spacing = 2.0;

    items.push({ id: 'mod-1', type: 1, y: currentY });
    currentY += spacing;

    items.push({ id: 'mod-2', type: 2, y: currentY });
    currentY += spacing;

    items.push({ id: 'mod-3', type: 3, y: currentY });
    currentY += spacing;

    for (let i = 0; i < comp3Count; i++) {
      items.push({ id: `mod-4-${i}`, type: 4, y: currentY, index: i });
      currentY += spacing;
    }

    items.push({ id: 'mod-5', type: 5, y: currentY });

    return items;
  }, [comp3Count]);

  const totalComponentCount = comp3Count + 4;
  const totalHeightMeters = totalComponentCount;
  const totalHeightMM = totalHeightMeters * 1000;

  useEffect(() => {
    const container = mountRef.current;
    if (!container) return;

    const width = container.clientWidth;
    const height = container.clientHeight;

    const scene = new THREE.Scene();
    scene.background = new THREE.Color('#090d16'); //'#090d16'
    sceneRef.current = scene;

    const stackGroup = new THREE.Group();
    scene.add(stackGroup);
    stackGroupRef.current = stackGroup;

    const dimsGroup = new THREE.Group();
    scene.add(dimsGroup);
    dimensionsGroupRef.current = dimsGroup;

    const aspect = width / height;
    const frustumHeight = Math.max(16, totalHeightMeters * 1.8);
    const orthoCam = new THREE.OrthographicCamera(
      (frustumHeight * aspect) / -2,
      (frustumHeight * aspect) / 2,
      frustumHeight / 2,
      frustumHeight / -2,
      -100,
      1000,
    );
    orthoCam.zoom = 1;
    orthoCam.position.set(20, 15, 20);
    cameraOrthoRef.current = orthoCam;

    const perspCam = new THREE.PerspectiveCamera(45, aspect, 0.1, 1000);
    perspCam.position.set(20, 15, 20);
    cameraPerspRef.current = perspCam;

    const currentCam = cameraMode === 'ORTHO' ? orthoCam : perspCam;
    activeCameraRef.current = currentCam;

    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: false });
    renderer.setSize(width, height);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    container.appendChild(renderer.domElement);
    rendererRef.current = renderer;

    const controls = new OrbitControls(currentCam, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.05;
    controls.enableRotate = true;
    controls.enableZoom = true;
    controls.enablePan = true;
    controls.minDistance = 2;
    controls.maxDistance = 200;
    controlsRef.current = controls;

    const ambLight = new THREE.AmbientLight(0xffffff, lightIntensity * 0.6);
    scene.add(ambLight);
    ambLightRef.current = ambLight;

    const dirLight = new THREE.DirectionalLight(0xffffff, lightIntensity * 1.5);
    dirLight.position.set(20, 30, 25);
    dirLight.castShadow = true;
    dirLight.shadow.mapSize.width = 2048;
    dirLight.shadow.mapSize.height = 2048;
    scene.add(dirLight);
    dirLightRef.current = dirLight;

    const pointLight = new THREE.PointLight(0x38bdf8, 0.4);
    pointLight.position.set(-10, -10, -10);
    scene.add(pointLight);

    const gridHelper = new THREE.GridHelper(30, 30, 0x3b82f6, 0x1e293b);
    gridHelper.position.y = -0.01;
    scene.add(gridHelper);
    gridHelperRef.current = gridHelper;

    const handleResize = () => {
      if (!container) return;
      const w = container.clientWidth;
      const h = container.clientHeight;
      const asp = w / h;

      const fH = Math.max(16, totalHeightMeters * 1.8);
      orthoCam.left = (fH * asp) / -2;
      orthoCam.right = (fH * asp) / 2;
      orthoCam.top = fH / 2;
      orthoCam.bottom = fH / -2;
      orthoCam.updateProjectionMatrix();

      perspCam.aspect = asp;
      perspCam.updateProjectionMatrix();

      renderer.setSize(w, h);
    };

    window.addEventListener('resize', handleResize);

    let animationFrameId;
    const animate = () => {
      animationFrameId = requestAnimationFrame(animate);

      // If user is interacting freely via OrbitControls, let controls handle camera updates smoothly.
      // We only apply programmatic preset lerps when a preset button is clicked.
      if (controlsRef.current) {
        controlsRef.current.autoRotate = autoRotate && viewPreset === 'ISO';
        controlsRef.current.autoRotateSpeed = 2.0;
        controlsRef.current.update();
      }

      // const cam = activeCameraRef.current;
      // if (cam && controlsRef.current) {
      //   cam.position.lerp(cameraTargetPos.current, 0.08);
      //   controlsRef.current.target.lerp(cameraTargetLookAt.current, 0.08);

      //   if (cam.isOrthographicCamera) {
      //     cam.zoom = THREE.MathUtils.lerp(
      //       cam.zoom,
      //       targetOrthoZoom.current,
      //       0.08,
      //     );
      //     cam.updateProjectionMatrix();
      //   }

      //   controlsRef.current.autoRotate = autoRotate && viewPreset === 'ISO';
      //   controlsRef.current.autoRotateSpeed = 2.0;
      //   controlsRef.current.update();
      // }

      if (stackGroupRef.current) {
        const explodeFactor = explodeView ? 0.8 : 0;
        stackGroupRef.current.children.forEach((child, idx) => {
          const targetY = child.userData.baseY + idx * explodeFactor;
          child.position.y = THREE.MathUtils.lerp(
            child.position.y,
            targetY,
            0.1,
          );
        });
      }

      renderer.render(scene, activeCameraRef.current);
    };

    animate();

    return () => {
      cancelAnimationFrame(animationFrameId);
      window.removeEventListener('resize', handleResize);
      if (renderer.domElement && container.contains(renderer.domElement)) {
        container.removeChild(renderer.domElement);
      }
      renderer.dispose();
    };
  }, []);

  useEffect(() => {
    if (!stackGroupRef.current) return;

    const stackGroup = stackGroupRef.current;

    while (stackGroup.children.length > 0) {
      const obj = stackGroup.children[0];
      stackGroup.remove(obj);
    }

    stackItems.forEach((item, idx) => {
      const modelInfo = modelSources[item.type];
      let meshGroup;

      if (modelInfo.isCustom && loadedGltfScenes.current[item.type]) {
        meshGroup = loadedGltfScenes.current[item.type].clone(true);
      } else {
        meshGroup = createProceduralGroup(item.type);
      }

      meshGroup.userData = { baseY: item.y, index: idx };
      meshGroup.position.set(0, item.y, 0);

      meshGroup.traverse((child) => {
        if (child.isMesh && child.material) {
          child.material = child.material.clone();
          child.material.wireframe = wireframe;
        }
      });

      stackGroup.add(meshGroup);
    });
  }, [stackItems, modelSources, wireframe]);

  const handleFileUpload = (type, event) => {
    const file = event.target.files[0];
    if (file) {
      const objectUrl = URL.createObjectURL(file);
      const loader = new GLTFLoader();

      loader.load(
        objectUrl,
        (gltf) => {
          loadedGltfScenes.current[type] = gltf.scene;
          setModelSources((prev) => ({
            ...prev,
            [type]: {
              ...prev[type],
              url: objectUrl,
              isCustom: true,
              fileName: file.name,
            },
          }));
        },
        undefined,
        (err) => {
          console.error('Failed to load custom GLB model', err);
        },
      );
    }
  };

  const resetModelUrl = (type) => {
    delete loadedGltfScenes.current[type];
    setModelSources((prev) => ({
      ...prev,
      [type]: { ...prev[type], url: '', isCustom: false, fileName: undefined },
    }));
  };

  useEffect(() => {
    if (!dimensionsGroupRef.current) return;
    const dimsGroup = dimensionsGroupRef.current;

    while (dimsGroup.children.length > 0) {
      dimsGroup.remove(dimsGroup.children[0]);
    }

    if (!showDimensions || explodeView) return;

    const totalH = totalHeightMeters;
    const material = new THREE.LineBasicMaterial({
      color: 0x3b82f6,
      linewidth: 2,
    });

    const points = [
      new THREE.Vector3(1.5, 0, 0),
      new THREE.Vector3(1.5, totalH, 0),
      new THREE.Vector3(1.35, 0, 0),
      new THREE.Vector3(1.65, 0, 0),
      new THREE.Vector3(1.35, totalH, 0),
      new THREE.Vector3(1.65, totalH, 0),
    ];

    const geometry = new THREE.BufferGeometry().setFromPoints(points);
    const lineSegments = new THREE.LineSegments(geometry, material);
    dimsGroup.add(lineSegments);
  }, [totalHeightMeters, showDimensions, explodeView]);

  useEffect(() => {
    if (ambLightRef.current)
      ambLightRef.current.intensity = lightIntensity * 0.6;
    if (dirLightRef.current)
      dirLightRef.current.intensity = lightIntensity * 1.5;
  }, [lightIntensity]);

  useEffect(() => {
    if (gridHelperRef.current) gridHelperRef.current.visible = showGrid;
  }, [showGrid]);

  // useEffect(() => {
  //   const activeCam =
  //     cameraMode === 'ORTHO' ? cameraOrthoRef.current : cameraPerspRef.current;
  //   activeCameraRef.current = activeCam;

  //   if (controlsRef.current && activeCam) {
  //     controlsRef.current.object = activeCam;
  //     controlsRef.current.update();
  //   }

  //   const effectiveHeight = totalHeightMeters * (explodeView ? 1.8 : 1.0);
  //   const centerY = effectiveHeight / 2;
  //   const baseDistance = Math.max(16, effectiveHeight * 2.2);

  //   //xxxx
  //   // cameraTargetLookAt.current.set(0, centerY, 0);
  //   if (controlsRef.current) {
  //     controlsRef.current.target.set(0, centerY, 0);
  //   }

  //   const targetPos = new THREE.Vector3();
  //   switch (viewPreset) {
  //     case 'FRONT':
  //       targetPos.current.set(0, centerY, baseDistance);
  //       break;
  //     case 'BACK':
  //       targetPos.current.set(0, centerY, -baseDistance);
  //       break;
  //     case 'LEFT':
  //       targetPos.current.set(-baseDistance, centerY, 0);
  //       break;
  //     case 'RIGHT':
  //       targetPos.current.set(baseDistance, centerY, 0);
  //       break;
  //     case 'TOP':
  //       targetPos.current.set(0, centerY + baseDistance, 0.0001);
  //       break;
  //     case 'BOTTOM':
  //       targetPos.current.set(0, centerY - baseDistance, 0.0001);
  //       break;
  //     case 'ISO':
  //     default:
  //       targetPos.current.set(
  //         baseDistance * 0.8,
  //         centerY + baseDistance * 0.5,
  //         baseDistance * 0.8,
  //       );
  //       break;
  //   }

  //   // targetOrthoZoom.current = Math.min(
  //   //   1.2,
  //   //   Math.max(0.25, 12 / effectiveHeight),
  //   // );
  //   if (activeCam) {
  //     activeCam.position.copy(targetPos);
  //     if (activeCam.isOrthographicCamera) {
  //       activeCam.zoom = Math.min(1.2, Math.max(0.25, 12 / effectiveHeight));
  //       activeCam.updateProjectionMatrix();
  //     }
  //   }

  //   if (cameraOrthoRef.current) {
  //     const container = mountRef.current;
  //     if (container) {
  //       const w = container.clientWidth;
  //       const h = container.clientHeight;
  //       const asp = w / h;
  //       const frustumHeight = Math.max(16, effectiveHeight * 1.8);

  //       cameraOrthoRef.current.left = (frustumHeight * asp) / -2;
  //       cameraOrthoRef.current.right = (frustumHeight * asp) / 2;
  //       cameraOrthoRef.current.top = frustumHeight / 2;
  //       cameraOrthoRef.current.bottom = frustumHeight / -2;
  //       cameraOrthoRef.current.updateProjectionMatrix();
  //     }
  //   }
  // }, [viewPreset, cameraMode, totalHeightMeters, explodeView]);

  useEffect(() => {
    const activeCam =
      cameraMode === 'ORTHO' ? cameraOrthoRef.current : cameraPerspRef.current;
    activeCameraRef.current = activeCam;

    if (controlsRef.current && activeCam) {
      controlsRef.current.object = activeCam;
      controlsRef.current.update();
    }

    const effectiveHeight = totalHeightMeters * (explodeView ? 1.8 : 1.0);
    const centerY = effectiveHeight / 2;
    const baseDistance = Math.max(16, effectiveHeight * 2.2);

    if (controlsRef.current) {
      controlsRef.current.target.set(0, centerY, 0);
    }

    const targetPos = new THREE.Vector3();
    switch (viewPreset) {
      case 'FRONT':
        targetPos.set(0, centerY, baseDistance);
        break;
      case 'BACK':
        targetPos.set(0, centerY, -baseDistance);
        break;
      case 'LEFT':
        targetPos.set(-baseDistance, centerY, 0);
        break;
      case 'RIGHT':
        targetPos.set(baseDistance, centerY, 0);
        break;
      case 'TOP':
        targetPos.set(0, centerY + baseDistance, 0.0001);
        break;
      case 'BOTTOM':
        targetPos.set(0, centerY - baseDistance, 0.0001);
        break;
      case 'ISO':
      default:
        targetPos.set(
          baseDistance * 0.8,
          centerY + baseDistance * 0.5,
          baseDistance * 0.8,
        );
        break;
    }

    if (activeCam) {
      activeCam.position.copy(targetPos);
      if (activeCam.isOrthographicCamera) {
        activeCam.zoom = Math.min(1.2, Math.max(0.25, 12 / effectiveHeight));
        activeCam.updateProjectionMatrix();
      }
    }

    if (controlsRef.current) {
      controlsRef.current.update();
    }

    if (cameraOrthoRef.current) {
      const container = mountRef.current;
      if (container) {
        const w = container.clientWidth;
        const h = container.clientHeight;
        const asp = w / h;
        const frustumHeight = Math.max(16, effectiveHeight * 1.8);

        cameraOrthoRef.current.left = (frustumHeight * asp) / -2;
        cameraOrthoRef.current.right = (frustumHeight * asp) / 2;
        cameraOrthoRef.current.top = frustumHeight / 2;
        cameraOrthoRef.current.bottom = frustumHeight / -2;
        cameraOrthoRef.current.updateProjectionMatrix();
      }
    }
  }, [viewPreset, cameraMode, totalHeightMeters, explodeView]);

  return (
    <div className="sv-container">
      <div ref={mountRef} className="sv-canvas" />

      {/* TOP HEADER */}
      <header className="sv-header">
        <div className="sv-card sv-header-title-card">
          <div className="sv-icon-badge">
            <Layers size={20} />
          </div>
          <div>
            <h1 className="sv-header-title">3D Stack Visualizer</h1>
            <p className="sv-header-subtitle">Orthographic & CAD View Studio</p>
          </div>
        </div>

        <div className="sv-card sv-header-info-card">
          <div className="sv-info-group">
            <Ruler className="icon icon-blue" />
            <span>Total Height:</span>
            <span className="sv-val-blue">
              {totalHeightMM.toLocaleString()} mm
            </span>
          </div>
          <div className="sv-divider-v" />
          <div className="sv-info-group">
            <Compass className="icon icon-amber" />
            <span>View:</span>
            <span className="sv-val-bold">
              {viewPreset} ({cameraMode})
            </span>
          </div>
        </div>
      </header>

      {/* TOP RIGHT TOOLBAR */}
      <div className="sv-card sv-top-controls">
        <button
          onClick={() => setCameraMode('ORTHO')}
          className={`sv-btn-pill ${cameraMode === 'ORTHO' ? 'active' : ''}`}
        >
          <Box size={14} /> Orthographic
        </button>
        <button
          onClick={() => setCameraMode('PERSP')}
          className={`sv-btn-pill ${cameraMode === 'PERSP' ? 'active' : ''}`}
        >
          <Video size={14} /> Perspective
        </button>
      </div>

      {/* SIDEBAR PANEL */}
      <aside className="sv-sidebar">
        <div className="sv-tabs">
          <button
            onClick={() => setActiveTab('views')}
            className={`sv-tab-btn ${activeTab === 'views' ? 'active' : ''}`}
          >
            <Compass size={14} /> Views
          </button>
          <button
            onClick={() => setActiveTab('controls')}
            className={`sv-tab-btn ${activeTab === 'controls' ? 'active' : ''}`}
          >
            <Sliders size={14} /> Controls
          </button>
          {/* <button
            onClick={() => setActiveTab('models')}
            className={`sv-tab-btn ${activeTab === 'models' ? 'active' : ''}`}
          >
            <Box size={14} /> Models
          </button>
          <button
            onClick={() => setActiveTab('info')}
            className={`sv-tab-btn ${activeTab === 'info' ? 'active' : ''}`}
          >
            <Info size={14} /> Specs
          </button> */}
        </div>

        <div className="sv-tab-content">
          {activeTab === 'views' && (
            <>
              <div>
                <span className="sv-section-title">Projection Mode</span>
                <div className="sv-toggle-box sv-grid-2">
                  <button
                    onClick={() => setCameraMode('ORTHO')}
                    className={`sv-btn-pill ${cameraMode === 'ORTHO' ? 'active' : ''}`}
                    style={{ justifyContent: 'center', padding: '8px' }}
                  >
                    <Box size={16} /> Orthographic
                  </button>
                  <button
                    onClick={() => setCameraMode('PERSP')}
                    className={`sv-btn-pill ${cameraMode === 'PERSP' ? 'active' : ''}`}
                    style={{ justifyContent: 'center', padding: '8px' }}
                  >
                    <Video size={16} /> Perspective
                  </button>
                </div>
              </div>

              <div>
                <span className="sv-section-title">
                  Orthographic View Presets
                </span>
                <div className="sv-grid-3">
                  <button
                    onClick={() => setViewPreset('FRONT')}
                    className={`sv-view-btn ${viewPreset === 'FRONT' ? 'active' : ''}`}
                  >
                    <ArrowUp size={16} />
                    <span>Front (Z+)</span>
                  </button>
                  <button
                    onClick={() => setViewPreset('BACK')}
                    className={`sv-view-btn ${viewPreset === 'BACK' ? 'active' : ''}`}
                  >
                    <ArrowDown size={16} />
                    <span>Back (Z-)</span>
                  </button>
                  <button
                    onClick={() => setViewPreset('LEFT')}
                    className={`sv-view-btn ${viewPreset === 'LEFT' ? 'active' : ''}`}
                  >
                    <ArrowLeft size={16} />
                    <span>Left (X-)</span>
                  </button>
                  <button
                    onClick={() => setViewPreset('RIGHT')}
                    className={`sv-view-btn ${viewPreset === 'RIGHT' ? 'active' : ''}`}
                  >
                    <ArrowRight size={16} />
                    <span>Right (X+)</span>
                  </button>
                  <button
                    onClick={() => setViewPreset('TOP')}
                    className={`sv-view-btn ${viewPreset === 'TOP' ? 'active' : ''}`}
                  >
                    <Layers size={16} />
                    <span>Top (Y+)</span>
                  </button>
                  <button
                    onClick={() => setViewPreset('BOTTOM')}
                    className={`sv-view-btn ${viewPreset === 'BOTTOM' ? 'active' : ''}`}
                  >
                    <GridIcon size={16} />
                    <span>Bottom (Y-)</span>
                  </button>
                </div>

                <button
                  onClick={() => setViewPreset('ISO')}
                  className={`sv-btn-full ${viewPreset === 'ISO' ? 'iso-active' : ''}`}
                  style={{ marginTop: '8px' }}
                >
                  <Compass size={16} /> 3D Isometric Free Orbit
                </button>
              </div>

              <div>
                <button
                  onClick={() => {
                    setViewPreset('ISO');
                    setAutoRotate(!autoRotate);
                  }}
                  className={`sv-btn-full ${autoRotate ? 'rotate-active' : ''}`}
                  style={{ justifyContent: 'space-between' }}
                >
                  <span
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                    }}
                  >
                    <RotateCw size={16} /> Auto-Rotate
                  </span>
                  <span className="sv-status-dot" />
                </button>
              </div>
            </>
          )}

          {activeTab === 'controls' && (
            <>
              <div className="sv-control-card">
                <div className="sv-label-row">
                  <label className="sv-label-text">
                    <span className="sv-pulse-dot" />
                    Component 3 Repetitions (N)
                  </label>
                  <span className="sv-badge-amber">
                    {comp3Count} {comp3Count === 1 ? 'unit' : 'units'}
                  </span>
                </div>

                <div className="sv-counter-row">
                  <button
                    onClick={() =>
                      setComp3Count((prev) => Math.max(1, prev - 1))
                    }
                    disabled={comp3Count <= 1}
                    className="sv-icon-btn"
                  >
                    <Minus size={16} />
                  </button>

                  <input
                    type="range"
                    min="1"
                    max="20"
                    value={comp3Count}
                    onChange={(e) =>
                      setComp3Count(parseInt(e.target.value) || 1)
                    }
                    className="sv-range-slider"
                  />

                  <button
                    onClick={() =>
                      setComp3Count((prev) => Math.min(20, prev + 1))
                    }
                    disabled={comp3Count >= 20}
                    className="sv-icon-btn"
                  >
                    <Plus size={16} />
                  </button>

                  <input
                    type="number"
                    min="1"
                    max="20"
                    value={comp3Count}
                    onChange={(e) =>
                      setComp3Count(
                        Math.min(
                          20,
                          Math.max(1, parseInt(e.target.value) || 1),
                        ),
                      )
                    }
                    className="sv-number-input"
                  />
                </div>
              </div>

              {/* <div>
                <span className="sv-section-title">Display Features</span>
                <div className="sv-grid-2">
                  <button
                    onClick={() => setExplodeView(!explodeView)}
                    className={`sv-feature-btn ${explodeView ? 'active-indigo' : ''}`}
                  >
                    <span
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                      }}
                    >
                      <Sparkles size={14} /> Explode
                    </span>
                    <span className="sv-status-dot" />
                  </button>

                  <button
                    onClick={() => setWireframe(!wireframe)}
                    className={`sv-feature-btn ${wireframe ? 'active-blue' : ''}`}
                  >
                    <span
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                      }}
                    >
                      <Eye size={14} /> Wireframe
                    </span>
                    <span className="sv-status-dot" />
                  </button>

                  <button
                    onClick={() => setShowGrid(!showGrid)}
                    className={`sv-feature-btn ${showGrid ? 'active-emerald' : ''}`}
                  >
                    <span
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                      }}
                    >
                      <GridIcon size={14} /> Grid
                    </span>
                    <span className="sv-status-dot" />
                  </button>

                  <button
                    onClick={() => setShowDimensions(!showDimensions)}
                    className={`sv-feature-btn ${showDimensions ? 'active-amber' : ''}`}
                  >
                    <span
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                      }}
                    >
                      <Ruler size={14} /> Ruler
                    </span>
                    <span className="sv-status-dot" />
                  </button>
                </div>
              </div> */}

              <div>
                <div
                  className="sv-label-row"
                  style={{
                    color: 'var(--text-muted)',
                    fontSize: '11px',
                    marginBottom: '6px',
                  }}
                >
                  <span
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                    }}
                  >
                    <Sun size={14} /> Light Intensity
                  </span>
                  <span>{(lightIntensity * 100).toFixed(0)}%</span>
                </div>
                <input
                  type="range"
                  min="0.2"
                  max="2.5"
                  step="0.1"
                  value={lightIntensity}
                  onChange={(e) =>
                    setLightIntensity(parseFloat(e.target.value))
                  }
                  className="sv-range-slider"
                  style={{ width: '100%' }}
                />
              </div>
            </>
          )}

          {activeTab === 'models' && (
            <>
              <p
                style={{
                  color: 'var(--text-muted)',
                  fontSize: '11px',
                  margin: 0,
                }}
              >
                Upload custom <code className="sv-code-badge">.glb</code> files
                to replace procedural models:
              </p>

              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '10px',
                }}
              >
                {[1, 2, 3, 4, 5].map((type) => {
                  const source = modelSources[type];
                  return (
                    <div key={type} className="sv-model-slot">
                      <div className="sv-model-info">
                        <div className="sv-model-title">
                          <span className="sv-badge-num">{type}</span>
                          Model {type}
                        </div>
                        <p className="sv-model-sub">
                          {source.isCustom
                            ? source.fileName
                            : 'Default Parametric Geometry'}
                        </p>
                      </div>

                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                        }}
                      >
                        {source.isCustom && (
                          <button
                            onClick={() => resetModelUrl(type)}
                            className="sv-icon-btn"
                            style={{ padding: '6px' }}
                          >
                            <RotateCcw size={14} />
                          </button>
                        )}

                        <label className="sv-upload-label">
                          <Upload size={14} />
                          <span>{source.isCustom ? 'Change' : 'Upload'}</span>
                          <input
                            type="file"
                            accept=".glb,.gltf"
                            onChange={(e) => handleFileUpload(type, e)}
                            style={{ display: 'none' }}
                          />
                        </label>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}

          {activeTab === 'info' && (
            <div
              style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}
            >
              <div className="sv-control-card">
                <h3
                  style={{
                    margin: 0,
                    fontSize: '12px',
                    fontWeight: '600',
                    color: '#f1f5f9',
                  }}
                >
                  Vertical Stacking Rules
                </h3>
                <ul
                  style={{
                    margin: 0,
                    paddingLeft: '16px',
                    color: '#cbd5e1',
                    fontSize: '11px',
                    lineHeight: '1.6',
                  }}
                >
                  <li>
                    <strong>Model 1:</strong> Ground Foundation (
                    <code className="sv-code-badge">Y = 0mm</code>)
                  </li>
                  <li>
                    <strong>Model 2:</strong> Sub-Assembly (
                    <code className="sv-code-badge">Y = 1000mm</code>)
                  </li>
                  <li>
                    <strong style={{ color: '#f59e0b' }}>
                      Model 3 (x{comp3Count}):
                    </strong>{' '}
                    Multiplier modules spaced 1000mm apart
                  </li>
                  <li>
                    <strong>Model 4:</strong> Upper Transition Segment
                  </li>
                  <li>
                    <strong>Model 5:</strong> Terminal Top Cap
                  </li>
                </ul>
              </div>
            </div>
          )}
        </div>

        <div className="sv-footer">
          <span className="sv-footer-text">Stack Height:</span>
          <span className="sv-footer-val">
            {totalHeightMM.toLocaleString()} mm ({totalHeightMeters}m)
          </span>
        </div>
      </aside>
    </div>
  );
}
